import React, { useEffect, useState } from 'react';
import { withAppLayout } from "components/core/withAppLayout";
import BlockMapper from "blocks/BlockMapper";
import CtaBlock, { ICtaBlock } from "blocks/cta/CtaBlock";
import { GetStaticPropsContext } from "next";
import { createSubscription } from "components/hooks/usePreview";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../../next-i18next.config";
import { allCustomerStoriesQuery, getCustomersPage } from "queries/Customers";
import { request } from "components/hooks/useQuery";
import useRouter from "components/hooks/useRouter";
import Container from "components/core/Container";
// import CustomerCard from "components/customers/CustomerCard";
import Heading from "components/typography/Heading";
import Text from "components/typography/Text";
import Button from "components/elements/Button";
import cn from "classnames";
import useTrans from "components/hooks/useTrans";
import { IImage } from "types/base";
import Card from 'components/base/Card';

export async function getStaticProps(context: GetStaticPropsContext) {
    const query = {
        query: getCustomersPage(context.locale),
        preview: context.preview,
        withAppData: true,
        locale: context.locale
    };

    const data = await createSubscription(context, query);

    return {
        props: {
            data: data,
            seo: data.initialData?.customer?.seo,
            appData: {
                ...data.initialData.header,
                ...data.initialData.footer,
            },
            ...(await serverSideTranslations(context.locale, ['common'], nextI18NextConfig)),
        }
    };
}

export type ICustomerCategory = {
    id: string;
    title: string;
};

export type ICustomerPage = {
    id: string;
    content: any;
    cta: ICtaBlock;
};

export type ICustomerStory = {
    id: string,
    title: string,
    slug?: string,
    hasDetailsPage?: boolean,
    featuredImage: IImage,
    intro: any,
    content: any,
    category?: ICustomerCategory[];
    hideForThisLanguage: boolean;
};

type Props = {
    customer: ICustomerPage;
    allCustomerStoryCategories: ICustomerCategory[];
};

function Index(props: Props) {
    const { customer, allCustomerStoryCategories } = props;
    const content = customer.content;
    const cta = customer.cta;
    const { t } = useTrans();
    const { locale } = useRouter();
    const [customerStories, setCustomerStories] = useState<ICustomerStory[]>([]);
    const [category, setCategory] = useState("all");
    const [pending, setPending] = useState(false);

    useEffect(() => {
        setPending(true);
        request({
            query: allCustomerStoriesQuery(locale)
        }).then((data) => {
            setCustomerStories(data.allCustomerStories);
        })
            .catch((err) => console.log(err))
            .finally(() => {
                setPending(false);
            });
    }, []);

    const CustomerCards = () => {
        const gridCn = cn(
            "grid grid-cols-1 gap-8 mt-4 lg:gap-6 lg:grid-cols-3 lg:gap-12 mb-10"
        );

        if (category == "all") {
            return (
                <div className={gridCn}>
                    {customerStories
                        .filter(cs => !cs.hideForThisLanguage)
                        .map(cs => (
                            <Card
                                key={cs.id}
                                title={cs.title}
                                featuredImage={cs.featuredImage}
                                eyebrow={cs.category.map(c => c.title).join(" - ")}
                                slug={cs.hasDetailsPage && (`/customers/` + cs.slug)}
                            />
                        ))}
                </div>
            );
        }

        return (
            <div className={gridCn}>
                {customerStories
                    .filter(cs => cs.category.find(c => category.includes(c.title)))
                    .filter(cs => !cs.hideForThisLanguage)
                    .map((cs: ICustomerStory) => (
                        <Card
                            key={cs.id}
                            title={cs.title}
                            featuredImage={cs.featuredImage}
                            eyebrow={cs.category.map(c => c.title).join(" - ")}
                            slug={cs.hasDetailsPage && (`/customers/` + cs.slug)}
                        />
                    ))}
                
            </div>
        );
    };

    return (
        <>
            {/* Quick fix, we need to change this once we have spacing objects...*/}
            {content.map((block: any, index: number) => Object.keys(block).length > 0 && (
                <div key={'content-item-' + index}>
                    <div className={"h-28"} hidden={index === 0}>&nbsp;</div>
                    <BlockMapper key={block.id} {...block} />
                </div>
            ))}

            <section>
                <Container>
                    <div className={"my-12"}>
                        <Text color={"text-purple"} weight={"medium"}>
                            {t('ourCustomers')}
                        </Text>
                        <Heading size={"lg"} weight={"semibold"}>{t('allCustomerCases')}</Heading>
                    </div>
                </Container>
            </section>

            <section>
                <Container className={"flex gap-x-4 mb-12"}>
                    <Button
                        id={"view-all-button"}
                        variant={category == "all" ? "primary" : "ghost"}
                        size={"small"}
                        color={"purple"}
                        className={"rounded"}
                        onClick={() => setCategory("all")}
                    >
                        {t('viewAll')}
                    </Button>
                    {allCustomerStoryCategories.filter(ac => ac.title).map((c: { id: string, title: string }) => (
                        <Button
                            key={c.id}
                            id={c.id}
                            variant={category == c.title ? "primary" : "ghost"}
                            color={"purple"}
                            size={"small"}
                            onClick={() => setCategory(c.title)}
                        >
                            {c.title}
                        </Button>
                    ))}
                </Container>
            </section>

            <section>
                <Container>
                   <CustomerCards />
                </Container>
            </section>

            {cta && (
                <section>
                    <CtaBlock {...cta} />
                </section>
            )}
        </>
    );
}

export default withAppLayout(Index);